// A row of major periods drawn to scale, with a "You are here" marker.
// The current period is tinted and the next one stands out, because the
// coming change is usually the most notable thing in a reader's timeline.

function toYear(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  return d.getUTCFullYear() + (d.getTime() - start) / (365.25 * 86400000);
}

// Below this share of the bar a long name like "Mercury" no longer fits;
// four-letter names (Mars, Rahu, Ketu) still do.
const NARROW = 0.12;

export function timelineLabel(periods, now) {
  return "Your major periods: " + periods
    .map((p) => {
      const span = `${p.lord} from ${p.start.slice(0, 4)} to ${p.end.slice(0, 4)}`;
      return p.start <= now && now < p.end ? `${span} (now)` : span;
    })
    .join(", then ") + ".";
}

export default function TimelineBar({ periods, now }) {
  if (!periods || periods.length === 0) return null;
  const first = toYear(periods[0].start);
  const last = toYear(periods[periods.length - 1].end);
  const share = (year) => (year - first) / (last - first);
  const pct = (year) => `${share(year) * 100}%`;
  const current = periods.findIndex((p) => p.start <= now && now < p.end);
  const nowYear = toYear(now);

  function tone(i) {
    if (i === current) return "bg-orange-100 text-orange-800";
    if (current >= 0 && i === current + 1) return "bg-orange-500 text-white";
    return "bg-gray-100 text-gray-500";
  }

  return (
    <figure role="img" aria-label={timelineLabel(periods, now)} className="space-y-1">
      <p className="text-xs font-medium text-gray-500">Your major periods</p>
      <div className="relative pt-6 pb-5" aria-hidden="true">
        <div className="flex h-9 overflow-hidden rounded-xl text-xs font-semibold">
          {periods.map((p, i) => {
            const width = share(toYear(p.end)) - share(toYear(p.start));
            return (
              <div
                key={`${p.lord}-${p.start}`}
                title={`${p.lord}, ${p.start.slice(0, 4)} to ${p.end.slice(0, 4)}`}
                className={`${tone(i)} grid place-items-center overflow-hidden border-r border-white last:border-r-0`}
                style={{ width: `${width * 100}%` }}
              >
                {width < NARROW && p.lord.length > 4 ? p.lord.slice(0, 3) : p.lord}
              </div>
            );
          })}
        </div>
        {current >= 0 ? (
          <>
            <span className="absolute top-5 bottom-5 w-0.5 -translate-x-1/2 bg-gray-900" style={{ left: pct(nowYear) }} />
            {/* The pill is kept inside the bar; the line marks the exact date. */}
            <span
              className="absolute top-0 -translate-x-1/2 whitespace-nowrap rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-medium text-white"
              style={{ left: `${Math.min(Math.max(share(nowYear), 0.12), 0.88) * 100}%` }}
            >
              You are here
            </span>
          </>
        ) : null}
        {periods.slice(1).map((p) => (
          <span
            key={p.start}
            className="absolute bottom-0 -translate-x-1/2 text-[10px] text-gray-500"
            style={{ left: pct(toYear(p.start)) }}
          >
            {p.start.slice(0, 4)}
          </span>
        ))}
      </div>
    </figure>
  );
}
